import { PATTERN, type PatternId } from '../../../render/patterns/patternIds';
import type { VertexStyle } from '../../mesh/meshBuilder';

/**
 * The surface patterns of the landmarks (STYLE §15; docs/plans/landmarks.md, "Surface patterns"), each
 * on the faces whose material a source names:
 * - the Oude Kerk's tower of about 1325 in its own brick, 27-29 cm, with a red sandstone quoin course
 *   every six courses (Janse 2004, the tower);
 * - brick banded with stone ('gespeckeerd'): the Oude Kerk's clerestory of 1510-12, whose masonry above
 *   the aisle roofs carries the bands (Janse, section 1.11), and the Nieuwe Kerk's walls but those of
 *   before 1452 to the east and south, its radiating chapels among them (De Gids 1942, p. 31), in De
 *   Graauw's late Gothic brick with the Oude Kerk's bands;
 * - De Graauw's late Gothic brick, measured on the Sint Antoniespoort, on that gate's walls, banded
 *   below 5.8 m with Gobertange on the hoofdpoort and its round towers (De Graauw, p. 121) and below
 *   5.4 m with Ledesteen on the voorpoort's towers (AAR 95, pp. 30-31), whose plinths are sandstone
 *   ashlar (AAR 95, p. 30);
 * - slate on the roofs, lead where the models clad in lead, and leaded glass in the church windows, at
 *   their presets' confidence.
 * Left plain, for no preset draws them yet: the Nieuwe Kerk's walls of before 1452 to the east and
 * south, of tuff (De Gids, p. 31); the Oude Kerk's chapels, whose south fronts are faced with Gobertange
 * stone and whose brick side walls band three and seven courses in turn (Janse, section 1.9), and its
 * choir and chapel walls whose material is not read. The town hall's close-view brick uses the
 * existing period house module as an estimate; its precise bond is not recovered from the painting.
 */
export const LANDMARK_PATTERN = {
  oudeKerkTower: PATTERN.brickOudeKerkTower,
  lateGothic: PATTERN.brickLateGothic,
  speklagen: PATTERN.brickLateGothicSpeklagen,
  gobertange: PATTERN.brickLateGothicGobertange,
  ledesteen: PATTERN.brickLateGothicLedesteen,
  sandstone: PATTERN.sandstoneAshlar,
  slate: PATTERN.slateMaas,
  lead: PATTERN.leadSheet,
  glass: PATTERN.leadedGlass,
} as const;

export function patterned(style: VertexStyle, pattern: PatternId): VertexStyle {
  return { ...style, pattern };
}

/** The same styles with their walls patterned; roofs and openings keep theirs. */
export function patternedWalls<S extends { readonly wall: VertexStyle }>(
  styles: S,
  pattern: PatternId
): S {
  return { ...styles, wall: patterned(styles.wall, pattern) };
}
